import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { Server, Database, Layers, Globe, Code, ShieldCheck } from "lucide-react";

// --- Helper Components ---

// StarRating Helper
const StarRating = ({ rating }: { rating: number }) => {
  const stars = [];
  for (let i = 1; i <= 5; i++) {
    stars.push(
      <svg
        key={i}
        className={`h-4 w-4 ${
          i <= rating ? "text-yellow-500" : "text-gray-300" 
        } inline-block`}
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.538 1.118l-2.8-2.034a1 1 0 00-1.176 0l-2.8 2.034c-.783.57-1.838-.197-1.538-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.381-1.81.588-1.81h3.462a1 1 0 00.95-.69l1.07-3.292z" />
      </svg>
    );
  }
  return <div className="flex items-center">{stars}</div>;
};

// Verified Badge
const VerifiedBadge = () => (
  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 ml-2 border border-gray-300">
    <svg
      className="w-3 h-3 mr-1 text-red-700"
      fill="currentColor"
      viewBox="0 0 20 20"
    >
      <path
        fillRule="evenodd"
        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
        clipRule="evenodd"
      />
    </svg>
    Verified
  </span>
);

// --- Tools Section (Advanced Java Specific) ---
const ToolsYouWillMaster = () => {
  const tools = [
    { name: 'Hibernate', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/hibernate/hibernate-original.svg' },
    { name: 'JDBC', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/java/java-original.svg' }, // Using Java logo for JDBC standard
    { name: 'Tomcat Server', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/tomcat/tomcat-original.svg' },
    { name: 'MySQL', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/mysql/mysql-original-wordmark.svg' },
    { name: 'Maven', logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/5/52/Apache_Maven_logo.svg/1200px-Apache_Maven_logo.svg.png' },
    { name: 'Eclipse EE', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/eclipse/eclipse-original.svg' },
    { name: 'Postman', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/postman/postman-original.svg' },
    { name: 'Log4j', logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/e/e4/Log4j_Logo.png/1200px-Log4j_Logo.png' },
  ];

  return (
    <section className="py-20 bg-gray-100">
      <div className="max-w-7xl mx-auto px-6 text-center">
        <h2 className="text-3xl md:text-4xl font-bold mb-12 text-black font-aligran">
          Enterprise Tools <span className="text-red-700">Stack</span>
        </h2>
        
        <div className="grid grid-cols-4 md:grid-cols-8 gap-8 mb-8">
          {tools.map((tool, i) => (
            <motion.div
              key={i}
              className="flex flex-col items-center justify-center p-4 bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.05 }}
            >
              <img
                src={tool.logo}
                alt={`${tool.name} Logo`}
                className="w-16 h-16 mb-2 object-contain"
              />
              <p className="text-sm font-semibold text-black mt-2">{tool.name}</p>
            </motion.div>
          ))}
        </div>
        
        <p className="text-gray-600 max-w-2xl mx-auto">
          We focus on the pure <b>J2EE</b> stack. Master <b>Hibernate ORM</b> for database handling and <b>Servlets/JSP</b> for server-side logic without framework abstractions.
        </p>
      </div>
    </section>
  );
};

const AdvancedJava = () => {
  // --- Data ---
  const testimonials = [
    {
      id: 1,
      name: "Vikram Rathore",
      review:
        "Understanding Hibernate mappings (One-to-One, Many-to-Many) was a game changer. Now I can handle complex databases easily.",
      rating: 5,
      date: "2024-08-22",
      profileImg: "https://randomuser.me/api/portraits/men/32.jpg", 
      source: "LinkedIn",
    },
    {
      id: 2,
      name: "Ananya Iyer",
      review:
        "Most institutes jump directly to Spring Boot, but this course taught me the real power of Servlets and JSP. My concepts are crystal clear.",
      rating: 5,
      date: "2024-08-15",
      profileImg: "https://randomuser.me/api/portraits/women/44.jpg", 
      source: "Google Reviews",
    },
    {
      id: 3,
      name: "Suresh Nair",
      review:
        "The project on 'Inventory Management' using MVC architecture helped me understand how enterprise applications are actually structured.",
      rating: 4,
      date: "2024-08-08",
      profileImg: "https://randomuser.me/api/portraits/men/55.jpg", 
      source: "Website Feedback",
    },
    {
      id: 4,
      name: "Deepika Singh",
      review:
        "JDBC and Connection Pooling were explained very well. I learned how to optimize database queries for better performance.",
      rating: 5,
      date: "2024-07-30",
      profileImg: "https://randomuser.me/api/portraits/women/68.jpg",
      source: "Google Reviews",
    },
    {
      id: 5,
      name: "Rahul Verma",
      review:
        "Perfect course for backend development. Maven and Log4j sessions were very practical and useful for my job.",
      rating: 4,
      date: "2024-07-25",
      profileImg: "https://randomuser.me/api/portraits/men/12.jpg",
      source: "LinkedIn",
    },
  ];

  const faqContent = [
    {
      title: "Why learn Servlets & JSP in the age of frameworks?",
      answer:
        "Frameworks like Spring rely on Servlets internally. Learning Servlets gives you a deep understanding of the HTTP request-response cycle, making you a better backend engineer.",
    },
    {
      title: "What is Hibernate and why use it?",
      answer:
        "Hibernate is an ORM (Object-Relational Mapping) tool. It allows you to map Java classes to database tables, eliminating the need to write complex SQL queries manually.",
    },
    {
      title: "Will we use Spring Boot?",
      answer:
        "No. This course focuses on 'Advanced Java' fundamentals (J2EE). Spring Boot is a separate framework. Mastering J2EE first makes learning Spring Boot much easier later.",
    },
    {
      title: "What is the MVC Architecture?",
      answer:
        "Model-View-Controller (MVC) is a design pattern used to separate business logic, UI, and data. We will build our projects using this industry-standard pattern.",
    },
    {
      title: "What projects will I build?",
      answer:
        "You will build a dynamic 'E-Commerce Backend' and a 'Student Portal' using JSP, Servlets, Hibernate, and MySQL.",
    },
  ];

  const curriculumContent = [
    {
      title: "Module 1: JDBC (Java Database Connectivity)",
      description: "Connecting Java to MySQL, CRUD operations, Prepared Statements, ResultSet, and Transaction Management.",
    },
    {
      title: "Module 2: Web Server & Deployment",
      description: "Understanding Apache Tomcat, Directory structure of a Web App (WAR files), and `web.xml` configuration.",
    },
    {
      title: "Module 3: Servlets (Server-Side Logic)",
      description: "Servlet Life Cycle, Request/Response objects, Session Management (Cookies, HttpSession), and Filters.",
    },
    {
      title: "Module 4: JSP (Java Server Pages)",
      description: "JSP Tags (Scriptlet, Expression), Implicit Objects, JSTL (Standard Tag Library), and creating dynamic web pages.",
    },
    {
      title: "Module 5: MVC Architecture",
      description: "Implementing Model-View-Controller pattern. Using Beans as Model, JSP as View, and Servlet as Controller.",
    },
    {
      title: "Module 6: Hibernate Core (ORM Basics)",
      description: "Introduction to ORM, Hibernate Architecture, Configuration (`hibernate.cfg.xml`), and Session Factory.",
    },
    {
      title: "Module 7: Hibernate Mappings & HQL",
      description: "Entity relationships (One-to-One, One-to-Many), HQL (Hibernate Query Language), and Criteria API.",
    },
    {
      title: "Module 8: Tools & Best Practices",
      description: "Build automation with Maven, Unit Testing with JUnit, Logging with Log4j, and Code Debugging.",
    },
  ];

  // Highlights Data with Advanced Java Specific Images
  const highlights = [
    { 
      title: 'Hibernate ORM', 
      desc: 'Map Java Objects to DB Tables.', 
      img: 'https://images.unsplash.com/photo-1544383835-bda2bc66a55d?q=80&w=2121' // Database/Mapping
    },
    { 
      title: 'Servlet Power', 
      desc: 'Master Request-Response Cycle.', 
      img: 'https://images.unsplash.com/photo-1558494949-ef526b0042a0?q=80&w=2070' // Server/Network
    },
    { 
      title: 'MVC Pattern', 
      desc: 'Industry standard architecture.', 
      img: 'https://images.unsplash.com/photo-1507238691740-187a5b1d37b8?q=80&w=2074' // Structure/Design
    },
    { 
      title: 'Database Design', 
      desc: 'Complex SQL & Relationships.', 
      img: 'https://images.unsplash.com/photo-1562577309-4932fdd64cd1?q=80&w=2074' // Charts/Data
    },
    { 
      title: 'Live Projects', 
      desc: 'Build E-commerce Backend.', 
      img: 'https://images.unsplash.com/photo-1556742049-0cfed4f7a07d?q=80&w=2070' // Payment/Shop
    },
    { 
      title: 'Maven Build', 
      desc: 'Automate project dependency.', 
      img: 'https://images.unsplash.com/photo-1618401471353-b98afee0b2eb?q=80&w=2088' // Code/Build
    },
    { 
      title: 'Expert Faculty', 
      desc: 'Learn from Senior Backend Devs.', 
      img: 'https://images.unsplash.com/photo-1531482615713-2afd69097998?q=80&w=2070' // Team
    },
    { 
      title: 'Certification', 
      desc: 'Advanced Java Developer.', 
      img: 'https://images.unsplash.com/photo-1589330273594-fade1ee91647?q=80&w=2070' // Certificate
    },
    { 
      title: 'Placement', 
      desc: 'Interview prep for Backend roles.', 
      img: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?q=80&w=2069' // Interview
    },
  ];

  // --- Logic for Carousel ---
  const [currentTestimonialIndex, setCurrentTestimonialIndex] = useState(0);
  const [visibleCount, setVisibleCount] = useState(3);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) setVisibleCount(3);
      else if (window.innerWidth >= 768) setVisibleCount(2);
      else setVisibleCount(1);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const autoScrollInterval = setInterval(() => {
      setCurrentTestimonialIndex((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(autoScrollInterval);
  }, [testimonials.length]);

  const currentVisibleTestimonials = [];
  for (let i = 0; i < visibleCount; i++) {
    currentVisibleTestimonials.push(
      testimonials[(currentTestimonialIndex + i) % testimonials.length]
    );
  }

  // --- Logic for Form ---
  const [formData, setFormData] = useState({ firstName: "", businessEmail: "", phone: "" });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    alert("Thank you! We will contact you shortly regarding the Advanced Java course.");
    setFormData({ firstName: "", businessEmail: "", phone: "" });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="bg-white text-gray-900 font-sans">
      <Helmet>
        <title>Advanced Java Course | Hibernate & Servlets</title>
        <meta name="description" content="Master Advanced Java (J2EE). Learn Hibernate, Servlets, JSP, and JDBC for Enterprise Backend Development." />
      </Helmet>

      {/* --- HERO SECTION --- */}
      <section className="relative overflow-hidden h-[550px] flex items-center bg-black"> 
      
      {/* Background Image + Gradient */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://images.unsplash.com/photo-1555099962-4199c345e5dd?q=80&w=2070&auto=format&fit=crop"
          alt="Backend Server"
          className="w-full h-full object-cover opacity-30"
        />
        {/* Red Gradient for Enterprise Java Theme */}
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/90 to-red-900/40" />
      </div>

      {/* Main Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-10 items-center w-full pt-20">

        {/* Left Content */}
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="inline-block px-3 py-1 mb-4 border border-red-500 rounded-full bg-red-900/30 text-red-400 text-sm font-semibold tracking-wide">
            ENTERPRISE BACKEND
          </div>

          <h1 className="text-4xl md:text-5xl font-extrabold leading-tight mb-6 text-gray-100 font-aligran">
            Advanced Java
            <span className="text-red-600 block">Master J2EE & Hibernate</span>
          </h1>

          <p className="text-xl text-gray-300 max-w-2xl mb-8">
            Build robust, scalable enterprise applications. Master <strong>Servlets</strong>, <strong>JSP</strong>, and the power of <strong>Hibernate ORM</strong>.
          </p>

          <button
            onClick={() =>
              document.getElementById("enquiry-form")?.scrollIntoView({ behavior: "smooth" })
            }
            className="bg-red-700 hover:bg-red-800 text-white font-bold py-4 px-10 rounded-lg shadow-[0_5px_20px_rgba(185,28,28,0.3)] transition duration-300 transform hover:scale-105"
          >
            Start Developing
          </button>
        </motion.div>

        {/* Right Side Image (Server/Java EE Concept) */}
        <motion.div
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.9 }}
          className="flex justify-center md:justify-end"
        >
          <img
            src="https://upload.wikimedia.org/wikipedia/en/thumb/3/30/Java_programming_language_logo.svg/1200px-Java_programming_language_logo.svg.png" 
            alt="Java EE Logo"
            className="w-56 md:w-64 
                       drop-shadow-[0_5px_30px_rgba(220,38,38,0.4)]
                       transition-transform duration-300 
                       hover:scale-105"
          />
        </motion.div>
      </div>
      </section>

      {/* --- WHY LEARN SECTION --- */}
      <section className="py-24 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-6 text-center">

          <h2 className="text-4xl md:text-5xl font-extrabold mb-4 text-gray-900 font-aligran">
            Why Learn <span className="text-red-700">Advanced Java?</span>
          </h2>

          <p className="text-gray-600 mb-14 max-w-3xl mx-auto text-lg leading-relaxed">
            It is the bridge between Core Java and Modern Frameworks. Learning J2EE makes you an expert in how web servers and databases interact.
          </p>

          <div className="grid md:grid-cols-3 gap-8">

            <div className="bg-white border border-gray-200 p-8 rounded-2xl shadow-md hover:shadow-xl hover:-translate-y-2 transition-all duration-300 group">
              <div className="w-14 h-14 bg-red-100 text-red-700 rounded-full flex items-center justify-center mb-5 mx-auto group-hover:scale-110 transition">
                <Server size={28} />
              </div>
              <h3 className="font-bold text-2xl text-black mb-3">Backend Mastery</h3>
              <p className="text-gray-600 leading-relaxed">
                Understand the Request-Response cycle, Session Management, and Cookies deeply.
              </p>
            </div>

            <div className="bg-white border border-gray-200 p-8 rounded-2xl shadow-md hover:shadow-xl hover:-translate-y-2 transition-all duration-300 group">
              <div className="w-14 h-14 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center mb-5 mx-auto group-hover:scale-110 transition">
                <Database size={28} />
              </div>
              <h3 className="font-bold text-2xl text-black mb-3">Hibernate Power</h3>
              <p className="text-gray-600 leading-relaxed">
                Stop writing raw SQL. Use Hibernate ORM to handle complex database operations with Java Objects.
              </p>
            </div>

            <div className="bg-white border border-gray-200 p-8 rounded-2xl shadow-md hover:shadow-xl hover:-translate-y-2 transition-all duration-300 group">
              <div className="w-14 h-14 bg-green-100 text-green-700 rounded-full flex items-center justify-center mb-5 mx-auto group-hover:scale-110 transition">
                <Layers size={28} />
              </div>
              <h3 className="font-bold text-2xl text-black mb-3">Enterprise Ready</h3>
              <p className="text-gray-600 leading-relaxed">
                Learn the MVC (Model-View-Controller) architecture used by large-scale banking and insurance apps.
              </p>
            </div>

          </div>
        </div>
      </section>

      {/* --- HIGHLIGHTS SECTION --- */}
      <section className="py-20 bg-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-gray-900 font-aligran">
            Course <span className="text-red-700">Highlights</span>
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {highlights.map((item, index) => (
              <motion.div
                key={index}
                className="bg-white rounded-xl shadow-md hover:shadow-lg transition overflow-hidden flex h-44 border-l-4 border-red-700"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
              >
                <div className="w-1/2 p-5 flex flex-col justify-center">
                  <h3 className="font-bold text-lg text-black mb-1">{item.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{item.desc}</p>
                </div>
                <div className="w-1/2 relative">
                  <img src={item.img} alt={item.title} className="h-full w-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-l from-black/40 to-transparent"></div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* --- CURRICULUM SECTION --- */}
      <section className="py-20 bg-white"> 
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-left text-gray-900 font-aligran"> 
            Detailed <span className="text-red-700">Curriculum</span>
          </h2>
          <div className="space-y-4">
            {curriculumContent.map((module, idx) => (
              <div key={idx} className="border border-gray-200 rounded-lg overflow-hidden bg-gray-50"> 
                <details className="group">
                  <summary className="cursor-pointer px-6 py-4 font-semibold text-lg bg-gray-100 hover:bg-gray-200 transition flex justify-between items-center text-gray-900"> 
                    {module.title}
                    <span className="transition-transform group-open:rotate-180 text-gray-900">▼</span>
                  </summary>
                  <div className="px-6 py-4 text-black border-t border-gray-200 leading-relaxed"> 
                    <p>{module.description}</p>
                  </div>
                </details>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* --- TOOLS SECTION --- */}
      <ToolsYouWillMaster />

      {/* --- FAQ & FORM SECTION --- */}
      <section className="max-w-7xl mx-auto px-6 py-20 grid md:grid-cols-3 gap-10 bg-white"> 
        <div className="md:col-span-2">
          <h2 className="text-3xl font-bold mb-8 text-gray-900 font-aligran">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {faqContent.map((item, idx) => (
              <div key={idx} className="bg-gray-50 border border-gray-200 rounded-lg"> 
                <details className="group p-4">
                  <summary className="cursor-pointer font-medium text-gray-900 hover:text-gray-700 flex justify-between">
                    {item.title}
                    <span className="text-gray-900 group-open:text-red-700">+</span>
                  </summary>
                  <p className="mt-4 text-black text-sm border-t border-gray-200 pt-3">{item.answer}</p> 
                </details>
              </div>
            ))}
          </div>
        </div>

        <div className="md:col-span-1" id="enquiry-form">
          <div className="bg-gray-100 border border-red-300 rounded-xl p-6 shadow-xl sticky top-10">
            <h3 className="text-2xl font-bold mb-2 text-center text-red-700">Book Your Seat</h3>
            <p className="text-center text-gray-600 mb-6 text-sm">Advance your Java Career.</p>
            <form onSubmit={handleSubmit} className="space-y-4">
              <input type="text" name="firstName" placeholder="Your Name" required className="w-full bg-white border border-gray-300 rounded p-3 text-black focus:border-red-500 focus:outline-none" value={formData.firstName} onChange={handleInputChange} />
              <input type="email" name="businessEmail" placeholder="Email Address" required className="w-full bg-white border border-gray-300 rounded p-3 text-black focus:border-red-500 focus:outline-none" value={formData.businessEmail} onChange={handleInputChange} />
              <input type="tel" name="phone" placeholder="Phone Number" required className="w-full bg-white border border-gray-300 rounded p-3 text-black focus:border-red-500 focus:outline-none" value={formData.phone} onChange={handleInputChange} />
              <button type="submit" className="w-full bg-gradient-to-r from-red-700 to-red-800 hover:from-red-800 hover:to-red-900 text-white font-bold py-3 rounded transition shadow-lg">ENQUIRE NOW</button>
              <p className="text-xs text-center text-gray-500 mt-2">We respect your privacy.</p>
            </form>
          </div>
        </div>
      </section>

      {/* --- TESTIMONIALS SECTION --- */}
      <section className="bg-gray-50 py-20 border-t border-gray-200"> 
        <div className="max-w-7xl mx-auto px-6 text-left"> 
            <h2 className="text-3xl md:text-4xl font-bold mb-12 text-gray-900 font-aligran">
                Success <span className="text-red-700">Stories</span>
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
                <AnimatePresence mode="popLayout">
                    {currentVisibleTestimonials.map((testimonial) => (
                        <motion.div
                            key={testimonial.id}
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.9 }}
                            transition={{ duration: 0.4 }}
                            className="bg-white p-6 rounded-xl border border-gray-200 flex flex-col text-left hover:border-red-400/50 transition shadow-sm"
                        >
                            <div className="flex items-center mb-4">
                                <img src={testimonial.profileImg} alt={testimonial.name} className="w-12 h-12 rounded-full border-2 border-red-500 mr-3" />
                                <div>
                                    <h4 className="font-bold text-black">{testimonial.name}</h4> 
                                    <span className="text-gray-500 text-xs">{testimonial.source}</span>
                                </div>
                            </div>
                            <div className="mb-3"><StarRating rating={testimonial.rating} /></div>
                            <p className="text-gray-700 text-sm flex-grow">"{testimonial.review}"</p> 
                            <VerifiedBadge />
                        </motion.div>
                    ))}
                </AnimatePresence>
            </div>
            <div className="flex justify-start mt-8 space-x-2"> 
                {testimonials.map((_, idx) => (
                    <button
                        key={idx}
                        onClick={() => setCurrentTestimonialIndex(idx)}
                        className={`h-2 w-2 rounded-full transition-all ${idx === currentTestimonialIndex ? 'bg-red-700 w-6' : 'bg-gray-300'}`}
                    />
                ))}
            </div>
        </div>
      </section>
    </div>
  );
};

export default AdvancedJava;